import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '../../../../components/common/Typography';
import { useTrans } from '../../../../services/i18n';
import { CRM_EMAIL_FREQUENCIES } from '../crmEmailFrequencies';

const TRACK_PADDING = 4;
const PILL_RADIUS = 6;

const useStyles = makeStyles((theme) => ({
  container: {
    marginLeft: '3.8rem',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(1.5),
  },
  track: {
    position: 'relative',
    display: 'inline-flex',
  },
  group: {
    padding: TRACK_PADDING,
    borderRadius: TRACK_PADDING + PILL_RADIUS,
  },
  grouped: {
    '&:not(:first-child)': { borderRadius: PILL_RADIUS },
    '&:not(:last-child)': { borderRadius: PILL_RADIUS },
  },
  option: {
    position: 'relative',
    zIndex: 1,
    borderRadius: PILL_RADIUS,
    '&.Mui-selected': {
      fontWeight: 600,
      backgroundColor: 'transparent',
      '&:hover': { backgroundColor: 'transparent' },
    },
  },
  indicator: {
    position: 'absolute',
    top: TRACK_PADDING,
    bottom: TRACK_PADDING,
    borderRadius: PILL_RADIUS,
    backgroundColor: theme.palette.secondary.main,
    pointerEvents: 'none',
    transition: theme.transitions.create(['left', 'width'], { duration: theme.transitions.duration.short }),
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
}));

export default function NotificationFrequencyField({ value, onChange, disabled }) {
  const { trans } = useTrans();
  const styles = useStyles();
  const trackRef = useRef(null);
  const optionRefs = useRef({});
  const [indicator, setIndicator] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      const option = optionRefs.current[value];
      if (!option) {
        setIndicator(null);
        return;
      }
      const next = { left: option.offsetLeft, width: option.offsetWidth };
      setIndicator((current) =>
        current && current.left === next.left && current.width === next.width ? current : next
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined' || !trackRef.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, [value]);

  const handleChange = (event, nextValue) => {
    if (nextValue !== null) onChange(nextValue);
  };

  return (
    <Grid container className={styles.container}>
      <Grid item xs={12}>
        <Typography variant="h6" weight="600" color="initial" className={styles.title}>
          {trans('notification-frequency')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <div ref={trackRef} className={styles.track}>
          {indicator && <span aria-hidden="true" className={styles.indicator} style={indicator} />}
          <ToggleButtonGroup
            exclusive
            value={value}
            onChange={handleChange}
            aria-label={trans('notification-frequency')}
            className={styles.group}
            classes={{ groupedHorizontal: styles.grouped }}
          >
            {CRM_EMAIL_FREQUENCIES.map(({ value: option, label }) => (
              <ToggleButton
                key={option}
                ref={(node) => {
                  optionRefs.current[option] = node;
                }}
                value={option}
                disabled={disabled}
                className={styles.option}
              >
                {trans(label)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
      </Grid>
    </Grid>
  );
}

NotificationFrequencyField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

NotificationFrequencyField.defaultProps = {
  disabled: false,
};
